import * as React from "react"

import { cn } from "@/lib/utils"

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
    containerClassName?: string;
}

function Table({
    className,
    containerClassName,
    ...props
}: TableProps) {
    return (
        <div data-slot="table-container" className={cn("relative w-full overflow-x-auto", containerClassName)}>
            <table
                data-slot="table"
                className={cn("w-full caption-bottom text-sm", className)}
                {...props} />
        </div>
    );
}

function TableHeader({
    className,
    ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
    return (
        <thead
            data-slot="table-header"
            className={cn("bg-slate-50/80 [&_tr]:border-b", className)}
            {...props} />
    );
}

function TableBody({
    className,
    ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
    return (
        <tbody
            data-slot="table-body"
            className={cn("[&_tr:last-child]:border-0", className)}
            {...props} />
    );
}

function TableFooter({
    className,
    ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
    return (
        <tfoot
            data-slot="table-footer"
            className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
            {...props} />
    );
}

function TableRow({
    className,
    ...props
}: React.HTMLAttributes<HTMLTableRowElement>) {
    return (
        <tr
            data-slot="table-row"
            className={cn(
                "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
                className
            )}
            {...props} />
    );
}

function TableHead({
    className,
    ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
    return (
        <th
            data-slot="table-head"
            className={cn(
                "h-9 px-3 text-left align-middle font-semibold uppercase text-[11px] tracking-wider whitespace-nowrap text-slate-600 bg-slate-50/80 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
                className
            )}
            {...props} />
    );
}

function TableCell({
    className,
    ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
    return (
        <td
            data-slot="table-cell"
            className={cn(
                "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
                className
            )}
            {...props} />
    );
}

function TableCaption({
    className,
    ...props
}: React.HTMLAttributes<HTMLTableCaptionElement>) {
    return (
        <caption
            data-slot="table-caption"
            className={cn("text-muted-foreground mt-4 text-sm", className)}
            {...props} />
    );
}

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    TableCaption,
}
